<template>
  <AppDialog :is-open="isDialogOpened"
             size="xxl"
             :ok-text="$t('common.share')"
             :ok-loading="shareTasksPending"
             :ok-disabled="shareTasksPending"
             :cancel-disabled="shareTasksPending"
             @ok="prepareShareTasks"
             @cancel="closeDialog"
  >
    <template #title>{{ $t('common.share') }}</template>
    <template #body>
      <div class="text--primary">
        <v-row class="mt-0">
          <v-col class="text-accent-darken-4">
            <h4>
              {{ $t('project.checklist.dialogs.ChecklistShareTasksDialog.selectedTasks', { tasksCount: tasksToShare.length }, tasksToShare.length) }}
              <span v-if="singleTask">: {{ singleTask.display }} {{ singleTask.title }}</span>
            </h4>
          </v-col>
        </v-row>
        <v-row class="mt-0">
          <v-col>
            {{ $t('project.checklist.dialogs.ChecklistShareTasksDialog.selectResponsibleAndObservers', tasksToShare.length) }}
          </v-col>
        </v-row>
        <v-row class="mt-0">
          <v-col>
            <Alert type="warning"
                   noIcon
                   :dismissible="false"
            >
              <div class="d-flex">
                <div class="font-weight-bold mr-2">{{$t('common.warning')}} :</div>
                <div class="d-flex flex-column row-gap-2">
                  <div>{{$t('project.checklist.dialogs.ChecklistShareTasksDialog.warningAboutObervers', tasksToShare.length)}}</div>
                  <div>{{$t('project.checklist.dialogs.ChecklistShareTasksDialog.warningAboutTitles')}}</div>
                </div>
              </div>
            </Alert>
          </v-col>
        </v-row>
        <v-row class="mt-0">
          <v-col>
            <v-checkbox v-model="taskVisibleByAll"
                        class="mt-0"
                        density="compact"
                        hide-details
                        :disabled="groups.length === 0"
                        :label="$t('project.checklist.dialogs.ChecklistShareTasksDialog.taskVisibleByAll', tasksToShare.length)"
            />
          </v-col>
        </v-row>
        <v-row class="mt-0">
          <v-col>
            <v-checkbox v-model="sendNotif"
                        density="compact"
                        hide-details
                        class="mt-0 pt-0"
                        :label="$t('project.checklist.dialogs.ChecklistShareTasksDialog.notifyNew')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <ChecklistShareTasksDialogRolePicker :mnemo="roomMnemo"
                                                 :taskVisibleByAll="taskVisibleByAll"
            />
          </v-col>
        </v-row>
      </div>
    </template>
  </AppDialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import Alert from '@/common/alerts/Alert.vue'
import AppDialog from '@/common/dialogs/AppDialog.vue'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import {
  CLEAR_ITEMS_SELECTION,
  GET_CHECKLIST,
  GET_TASK_DETAILS,
  SHARE_TASKS,
} from '@/store/modules/checklist/action_types'

import ChecklistShareTasksDialogRolePicker from './ChecklistShareTasksDialogRolePicker.vue'

export default {
  name: 'ChecklistShareTasksDialog',
  components: { 
    Alert,
    AppDialog,
    ChecklistShareTasksDialogRolePicker,
  },
  mixins: [dialogMixin],
  data () {
    return {
      sendNotif: true,
      taskVisibleByAll: false,
    }
  },
  computed: {
    ...mapState('checklist', [
      'currentTaskFolder',
      'shareTaskRolePickerSelectedObservers',
      'shareTaskRolePickerSelectedResponsibles',
      'shareTaskRolePickerSelectedValidators',
      'shareTasksDialog',
      'shareTasksPending',
    ]),
    ...mapState('groups', ['groups']),
    ...mapGetters('room', ['roomMnemo']),
    tasksToShare () { return this.shareTasksDialog.tasksToShare },
    singleTask () {
      return this.tasksToShare.length === 1
        ? this.tasksToShare[0]
        : null
    },
  },
  created () {
    if (this.singleTask) {
      this.taskVisibleByAll = this.singleTask.rights.shareType === 'public'
    } else {
      this.taskVisibleByAll = this.tasksToShare.every(task => task.rights.shareType === 'public')
    }
  },
  methods: {
    ...mapActions('checklist', [CLEAR_ITEMS_SELECTION, SHARE_TASKS, GET_TASK_DETAILS, GET_CHECKLIST]),
    async prepareShareTasks () {
      try {
        await this.SHARE_TASKS({
          mnemo: this.roomMnemo,
          data: {
            tasks: this.tasksToShare.map(item => { return item.id }),
            isPublicRead: this.taskVisibleByAll,
            rightsUsersResponsibles: this.shareTaskRolePickerSelectedResponsibles.members.map(item => item.id),
            rightsGroupsResponsibles: this.shareTaskRolePickerSelectedResponsibles.groups.map(item => item.id),
            rightsUsersObservers: this.shareTaskRolePickerSelectedObservers.members.map(item => item.id),
            rightsGroupsObservers: this.shareTaskRolePickerSelectedObservers.groups.map(item => item.id),
            rightsUsersValidators: this.shareTaskRolePickerSelectedValidators.members.map(item => item.id),
            rightsGroupsValidators: this.shareTaskRolePickerSelectedValidators.groups.map(item => item.id),
            notify: this.sendNotif,
          },
        })

        toast.success(this.$t('project.checklist.dialogs.ChecklistShareTasksDialog.shareTasksSuccess', this.tasksToShare.length))

        if (this.singleTask) {
          this.GET_TASK_DETAILS({
            mnemo: this.roomMnemo,
            taskId: this.singleTask.id,
          })
        } else {
          this.GET_CHECKLIST(this.roomMnemo)
        }

        this.CLEAR_ITEMS_SELECTION()
      } catch (e) {
        toast.error(this.$t('project.checklist.dialogs.ChecklistShareTasksDialog.shareTasksError', this.tasksToShare.length))
      } finally {
        this.closeDialog()
      }
    },
  },
}
</script>
